import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import CustomStore from 'devextreme/data/custom_store';
import notify from 'devextreme/ui/notify';
import {
  DataGrid,
  Column,
  SearchPanel,
  Toolbar,
  Item,
  Export,
  Selection,
  Scrolling,
  LoadPanel
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import Form from 'devextreme-react/form';
import { getApiToken } from '../../utils/authUtils';
import './user-list.scss';

const initialFormState = {
  displayName: '',
  mail: '',
  isEntraUser: false,
  allowCreateCompetition: false,
  status: 'active'
};

const buildCreateFormItems = () => ([
  {
    dataField: 'displayName',
    label: { text: 'Name' },
    editorType: 'dxTextBox',
    isRequired: true,
    editorOptions: { stylingMode: 'filled', placeholder: 'Anzeigename' }
  },
  {
    dataField: 'mail',
    label: { text: 'E-Mail' },
    editorType: 'dxTextBox',
    editorOptions: { stylingMode: 'filled', placeholder: 'benutzer@example.com' }
  },
  {
    dataField: 'status',
    label: { text: 'Status' },
    editorType: 'dxTextBox',
    editorOptions: { stylingMode: 'filled', placeholder: 'z. B. active' }
  },
  {
    dataField: 'allowCreateCompetition',
    label: { text: 'Wettkämpfe erstellen erlaubt' },
    editorType: 'dxSwitch',
    editorOptions: { switchedOnText: 'Ja', switchedOffText: 'Nein' }
  },
  {
    dataField: 'isEntraUser',
    label: { text: 'Entra-ID Benutzer' },
    editorType: 'dxSwitch',
    editorOptions: { switchedOnText: 'Ja', switchedOffText: 'Nein' }
  }
]);

const createUserStore = (token) => createStore({
  key: 'userId',
  loadUrl: `${process.env.REACT_APP_serverEndpoint}/User/Users`,
  onBeforeSend: (method, ajaxOptions) => {
    ajaxOptions.headers = { Authorization: `Bearer ${token}` };
  }
});

const UserListPage = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [dataSource, setDataSource] = useState(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const formItems = useMemo(buildCreateFormItems, []);

  useEffect(() => {
    getApiToken(instance, accounts)
      .then((token) => setDataSource(createUserStore(token)))
      .catch(() => notify('Keine Berechtigung zum Laden der Benutzer', 'error', 3000));
  }, [accounts, instance]);

  const refresh = useCallback(() => {
    gridRef.current?.instance?.refresh();
  }, []);

  const onOpen = useCallback((data) => {
    navigate(`/user/edit/${data.userId}`);
  }, [navigate]);

  const onCreate = useCallback(async () => {
    setSubmitting(true);
    try {
      const token = await getApiToken(instance, accounts);
      const payload = new FormData();
      payload.append('values', JSON.stringify(formData));

      const response = await fetch(`${process.env.REACT_APP_serverEndpoint}/User/InsertUser`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: payload
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Anlegen fehlgeschlagen');
      }

      notify('Benutzer wurde angelegt', 'success', 2000);
      setCreateVisible(false);
      setFormData(initialFormState);
      refresh();
    } catch (error) {
      notify(error.message || 'Anlegen fehlgeschlagen', 'error', 3000);
    } finally {
      setSubmitting(false);
    }
  }, [accounts, formData, instance, refresh]);

  return (
    <div className='view user-list'>
      <div className='view-wrapper view-wrapper-user-list list-page'>
        <DataGrid
          dataSource={dataSource}
          ref={gridRef}
          className='grid theme-dependent'
          showBorders
          hoverStateEnabled
          onRowDblClick={({ data }) => onOpen(data)}
          height='100%'
          remoteOperations={true}
        >
          <LoadPanel showPane={false} />
          <SearchPanel visible placeholder='Benutzer suchen' />
          <Export enabled formats={['xlsx', 'csv']} allowExportSelectedData />
          <Selection mode='single' />
          <Scrolling mode='standard' />

          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Benutzer</div>
            </Item>
            <Item location='after'>
              <Button
                icon='plus'
                text='Neu'
                type='default'
                stylingMode='contained'
                onClick={() => setCreateVisible(true)}
              />
            </Item>
            <Item location='after'>
              <Button
                icon='refresh'
                text='Aktualisieren'
                stylingMode='text'
                onClick={refresh}
              />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='displayName' caption='Name' dataType='string' minWidth={200} />
          <Column dataField='mail' caption='E-Mail' dataType='string' minWidth={200} />
          <Column dataField='status' caption='Status' dataType='string' width={120} />
          <Column dataField='allowCreateCompetition' caption='Wettkämpfe anlegen' dataType='boolean' width={150} />
          <Column dataField='isEntraUser' caption='Entra-ID' dataType='boolean' width={90} />
          <Column dataField='isDisabled' caption='Deaktiviert' dataType='boolean' width={110} />
        </DataGrid>
      </div>

      <Popup
        visible={createVisible}
        dragEnabled
        onHiding={() => setCreateVisible(false)}
        showCloseButton
        showTitle
        title='Neuer Benutzer'
        width={520}
      >
        <div className='create-form'>
          <Form
            formData={formData}
            items={formItems}
            labelLocation='top'
            onFieldDataChanged={(e) => setFormData({ ...formData, [e.dataField]: e.value })}
          />
          <div className='popup-actions'>
            <Button
              text='Abbrechen'
              stylingMode='outlined'
              onClick={() => setCreateVisible(false)}
            />
            <Button
              text='Speichern'
              type='default'
              icon='check'
              disabled={submitting || !formData.displayName}
              onClick={onCreate}
              loadingIndicatorEnabled={submitting}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default UserListPage;
